import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Gauge, Globe, Leaf, PenLine, Plus, Save, Search, Trash2, TrendingUp, X, Zap } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  altaParticipanteMem,
  borrarPosicionMercado,
  capturarPosicionMercado,
  getParticipantesMem,
  getPosicionesMercado,
  type LadoPosicion,
  type ParticipanteMem,
  type PosicionMercado,
  type ProductoPosicion,
} from "@/lib/api";

const PRODUCTOS: { key: ProductoPosicion; label: string; unidad: string; decimales: number }[] = [
  { key: "energia", label: "Energía", unidad: "MWh/año", decimales: 0 },
  { key: "potencia", label: "Potencia", unidad: "MW", decimales: 3 },
  { key: "cel", label: "CEL", unidad: "CEL/año", decimales: 0 },
  { key: "irec", label: "i-REC", unidad: "i-REC/año", decimales: 0 },
];
const prod = (k: ProductoPosicion) => PRODUCTOS.find((p) => p.key === k)!;

const LADOS: { key: LadoPosicion; label: string; detalle: string }[] = [
  { key: "demanda", label: "Demanda", detalle: "el participante compra · MPP le vendería" },
  { key: "oferta", label: "Oferta", detalle: "el participante vende · MPP le compraría" },
];

const HORAS_ANIO = 8760;
const ANIO_ACTUAL = new Date().getFullYear();

type Form = {
  participante: string;
  lado: LadoPosicion;
  producto: ProductoPosicion;
  anioDesde: string;
  anioHasta: string;
  cantidad: string;
  notas: string;
  id: number | null;          // posición existente que se está editando (para borrar)
};

const FORM_VACIO = (producto: ProductoPosicion): Form => ({
  participante: "", lado: "demanda", producto,
  anioDesde: String(ANIO_ACTUAL + 1), anioHasta: String(ANIO_ACTUAL + 1),
  cantidad: "", notas: "", id: null,
});

/**
 * Posiciones de mercado: oferta y demanda anual de los participantes del MEM
 * (análisis de mercado, no contratos). Reemplaza el Excel "POSICIONES DE
 * MERCADO" (hoja MEM). Se captura participante (catálogo CENACE), lado,
 * producto, cantidad y un rango de años; la matriz muestra por producto la
 * demanda, la oferta y el neto de cada año. Clic en una celda = editarla ahí
 * mismo (Enter guarda, Tab guarda y pasa al año siguiente, Esc cancela, vacía =
 * borrar); el formulario queda para rangos de años, notas y participantes nuevos.
 */
export function PosicionesMercadoPage() {
  const qc = useQueryClient();
  const participantes = useQuery({ queryKey: ["participantes-mem"], queryFn: getParticipantesMem });
  const posiciones = useQuery({ queryKey: ["posiciones-mercado"], queryFn: getPosicionesMercado });

  const [producto, setProducto] = useState<ProductoPosicion>("energia");
  const [form, setForm] = useState<Form>(FORM_VACIO("energia"));
  const [aviso, setAviso] = useState<{ ok: boolean; texto: string } | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const refrescar = () => {
    qc.invalidateQueries({ queryKey: ["posiciones-mercado"] });
    qc.invalidateQueries({ queryKey: ["participantes-mem"] });
  };

  const guardar = useMutation({
    mutationFn: () => capturarPosicionMercado({
      participante: form.participante,
      lado: form.lado,
      producto: form.producto,
      anio_desde: Number(form.anioDesde),
      anio_hasta: Number(form.anioHasta),
      cantidad: Number(form.cantidad),
      notas: form.notas.trim() || null,
    }),
    onSuccess: (r) => {
      refrescar();
      setProducto(form.producto);
      setAviso({
        ok: true,
        texto: `${form.participante}: ${r.guardados} año(s) guardado(s)` +
          (r.reemplazados ? ` · ${r.reemplazados} reemplazado(s)` : "") + ".",
      });
      setForm((f) => ({ ...f, cantidad: "", notas: "", id: null }));
    },
  });

  const borrar = useMutation({
    mutationFn: (id: number) => borrarPosicionMercado(id),
    onSuccess: () => {
      refrescar();
      setAviso({ ok: true, texto: "Posición borrada." });
      setForm((f) => ({ ...f, cantidad: "", notas: "", id: null }));
    },
  });

  /** Edición en línea de UNA celda de la matriz: guarda el año o, si se dejó
   *  vacía una celda con cifra, la borra. Conserva las notas de la celda. */
  const guardarCelda = useMutation({
    mutationFn: async (c: CambioCelda) => {
      if (c.valor === null) {
        if (c.existente) await borrarPosicionMercado(c.existente.id);
        return;
      }
      await capturarPosicionMercado({
        participante: c.participante, lado: c.lado, producto: c.producto,
        anio_desde: c.anio, anio_hasta: c.anio, cantidad: c.valor,
        notas: c.existente?.notas ?? null,
      });
    },
    onSuccess: (_r, c) => {
      refrescar();
      const u = prod(c.producto);
      setAviso({
        ok: true,
        texto: c.valor === null
          ? `${c.participante} ${c.anio}: borrada.`
          : `${c.participante} ${c.anio}: ${fmt(c.valor, u.decimales)} ${u.unidad} guardado.`,
      });
    },
    onError: (e) => setAviso({ ok: false, texto: (e as Error).message }),
  });

  const items = posiciones.data?.items ?? [];

  /** Abre una celda en el formulario (para notas o rango de años). */
  const editarCelda = (participante: string, lado: LadoPosicion, anio: number) => {
    const p = items.find((x) => x.participante === participante && x.lado === lado &&
      x.producto === producto && x.anio === anio);
    setForm({
      participante, lado, producto,
      anioDesde: String(anio), anioHasta: String(anio),
      cantidad: p ? String(p.cantidad) : "", notas: p?.notas ?? "", id: p?.id ?? null,
    });
    setAviso(null);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const desde = Number(form.anioDesde);
  const hasta = Number(form.anioHasta);
  const cantidad = Number(form.cantidad);
  const valido = !!form.participante && Number.isInteger(desde) && Number.isInteger(hasta) &&
    desde >= 2000 && hasta <= 2100 && hasta >= desde && cantidad > 0;
  const numAnios = valido ? hasta - desde + 1 : 0;
  const yaCapturados = valido
    ? items.filter((x) => x.participante === form.participante && x.lado === form.lado &&
        x.producto === form.producto && x.anio >= desde && x.anio <= hasta).length
    : 0;

  return (
    <div className="space-y-4 max-w-7xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-slate-500" /> Posiciones de mercado
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Oferta y demanda anual de los participantes del MEM (análisis de mercado, no son
          contratos). <b>Demanda</b> = el participante quiere comprar; <b>Oferta</b> = quiere
          vender. Las cantidades se capturan en positivo.
        </p>
      </div>

      {/* ---------------- Captura ---------------- */}
      <div ref={formRef} className="bg-white border rounded-lg p-4 space-y-3 scroll-mt-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">
            {form.id ? "Editar posición" : "Capturar posición"}
          </h2>
          {(form.participante || form.cantidad) && (
            <button type="button" onClick={() => { setForm(FORM_VACIO(producto)); setAviso(null); }}
              className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
              <X className="w-3.5 h-3.5" /> Limpiar
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
          <div className="md:col-span-5">
            <Etiqueta>Participante (catálogo CENACE)</Etiqueta>
            <ComboParticipante
              opciones={participantes.data ?? []}
              cargando={participantes.isLoading}
              valor={form.participante}
              onChange={(clave) => setForm((f) => ({ ...f, participante: clave, id: null }))}
              onAlta={(p) => {
                qc.setQueryData<ParticipanteMem[]>(["participantes-mem"],
                  (prev) => [...(prev ?? []), p].sort((a, b) => a.clave.localeCompare(b.clave)));
                setForm((f) => ({ ...f, participante: p.clave, id: null }));
              }}
            />
          </div>

          <div className="md:col-span-3">
            <Etiqueta>Lado</Etiqueta>
            <div className="grid grid-cols-2 rounded border border-slate-300 overflow-hidden text-sm">
              {LADOS.map((l) => (
                <button key={l.key} type="button" title={l.detalle}
                  onClick={() => setForm((f) => ({ ...f, lado: l.key, id: null }))}
                  className={`px-2 py-1.5 ${form.lado === l.key
                    ? (l.key === "demanda" ? "bg-rose-600 text-white" : "bg-emerald-600 text-white")
                    : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {l.label}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-slate-400 mt-1">
              {LADOS.find((l) => l.key === form.lado)!.detalle}
            </div>
          </div>

          <div className="md:col-span-4">
            <Etiqueta>Producto</Etiqueta>
            <select value={form.producto}
              onChange={(e) => setForm((f) => ({ ...f, producto: e.target.value as ProductoPosicion, id: null }))}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm">
              {PRODUCTOS.map((p) => (
                <option key={p.key} value={p.key}>{p.label} ({p.unidad})</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3">
            <Etiqueta>Cantidad ({prod(form.producto).unidad})</Etiqueta>
            <input type="number" min={0} step="any" value={form.cantidad}
              onChange={(e) => setForm((f) => ({ ...f, cantidad: e.target.value }))}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-right tabular-nums" />
            {cantidad > 0 && form.producto === "energia" && (
              <div className="text-[11px] text-slate-400 mt-1">
                ≈ {fmt(cantidad / HORAS_ANIO, 2)} MW promedio (carga plana)
              </div>
            )}
            {cantidad > 0 && form.producto === "potencia" && (
              <div className="text-[11px] text-slate-400 mt-1">
                ≈ {fmt(cantidad * HORAS_ANIO, 0)} MWh/año a carga plana
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <Etiqueta>Año desde</Etiqueta>
            <input type="number" min={2000} max={2100} value={form.anioDesde}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, anioDesde: v, id: null,
                  anioHasta: Number(f.anioHasta) < Number(v) ? v : f.anioHasta }));
              }}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm tabular-nums" />
          </div>
          <div className="md:col-span-2">
            <Etiqueta>Año hasta</Etiqueta>
            <input type="number" min={2000} max={2100} value={form.anioHasta}
              onChange={(e) => setForm((f) => ({ ...f, anioHasta: e.target.value, id: null }))}
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm tabular-nums" />
          </div>

          <div className="md:col-span-5">
            <Etiqueta>Notas (opcional)</Etiqueta>
            <input value={form.notas} maxLength={2000}
              onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
              placeholder="Fuente, supuesto, factor de planta…"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button type="button" onClick={() => guardar.mutate()} disabled={!valido || guardar.isPending}
            className="inline-flex items-center gap-1.5 rounded bg-slate-900 text-white px-3 py-1.5 text-sm hover:bg-slate-800 disabled:opacity-50">
            <Save className="w-4 h-4" />
            {guardar.isPending ? "Guardando…"
              : numAnios > 1 ? `Guardar ${numAnios} años` : "Guardar"}
          </button>
          {form.id && (
            <button type="button" disabled={borrar.isPending}
              onClick={() => {
                if (window.confirm(`¿Borrar ${prod(form.producto).label} ${form.anioDesde} de ${form.participante} (${form.lado})?`)) {
                  borrar.mutate(form.id!);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded border border-red-200 bg-white text-red-600 px-3 py-1.5 text-sm hover:bg-red-50 disabled:opacity-50">
              <Trash2 className="w-4 h-4" /> Borrar
            </button>
          )}
          {yaCapturados > 0 && (
            <span className="text-xs text-amber-700">
              {yaCapturados} de esos año(s) ya tienen cifra: se reemplaza.
            </span>
          )}
        </div>

        {guardar.isError && <Alerta texto={(guardar.error as Error).message} />}
        {borrar.isError && <Alerta texto={(borrar.error as Error).message} />}
        {aviso && (
          <div className={`rounded p-2.5 text-sm border ${aviso.ok
            ? "bg-emerald-50 border-emerald-200 text-emerald-800"
            : "bg-amber-50 border-amber-200 text-amber-800"}`}>
            {aviso.texto}
          </div>
        )}
      </div>

      {/* ---------------- Matriz ---------------- */}
      <div className="flex flex-wrap items-center gap-1 border-b">
        {PRODUCTOS.map((p) => {
          const n = items.filter((x) => x.producto === p.key).length;
          return (
            <button key={p.key} type="button" onClick={() => setProducto(p.key)}
              className={`px-3 py-2 text-sm -mb-px border-b-2 ${producto === p.key
                ? "border-slate-900 text-slate-900 font-medium"
                : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {p.label}
              {n > 0 && <span className="ml-1.5 text-xs text-slate-400">{n}</span>}
            </button>
          );
        })}
      </div>

      {posiciones.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>
      )}
      {posiciones.isError && <Alerta texto={(posiciones.error as Error).message} />}
      {posiciones.isSuccess && (
        <Matriz items={items.filter((x) => x.producto === producto)} producto={producto}
          seleccion={form.producto === producto ? form : null} onCelda={editarCelda}
          guardando={guardarCelda.isPending}
          onGuardarCelda={(c) => guardarCelda.mutate(c)} />
      )}

      {posiciones.isSuccess && (
        <Grafica items={items} producto={producto} onProducto={setProducto} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

type CambioCelda = {
  participante: string;
  lado: LadoPosicion;
  producto: ProductoPosicion;
  anio: number;
  valor: number | null;            // null = borrar
  existente: PosicionMercado | undefined;
};
type CeldaId = { clave: string; lado: LadoPosicion; anio: number };

function Matriz({ items, producto, seleccion, onCelda, onGuardarCelda, guardando }: {
  items: PosicionMercado[];
  producto: ProductoPosicion;
  seleccion: Form | null;
  onCelda: (participante: string, lado: LadoPosicion, anio: number) => void;
  onGuardarCelda: (c: CambioCelda) => void;
  guardando: boolean;
}) {
  const { unidad, decimales } = prod(producto);
  const [editando, setEditando] = useState<CeldaId | null>(null);
  useEffect(() => setEditando(null), [producto]);

  const { anios, filas, totales } = useMemo(() => {
    const ys = items.map((x) => x.anio);
    const min = ys.length ? Math.min(...ys) : ANIO_ACTUAL;
    const max = ys.length ? Math.max(...ys) : ANIO_ACTUAL + 5;
    const anios = Array.from({ length: max - min + 1 }, (_, i) => min + i);

    // participante+lado -> { razón, por año }
    const filas: Record<LadoPosicion, { clave: string; razon: string; valores: Map<number, PosicionMercado>; total: number }[]> =
      { demanda: [], oferta: [] };
    const totales: Record<LadoPosicion, Map<number, number>> = { demanda: new Map(), oferta: new Map() };
    for (const lado of ["demanda", "oferta"] as const) {
      const porPart = new Map<string, { clave: string; razon: string; valores: Map<number, PosicionMercado>; total: number }>();
      for (const x of items.filter((i) => i.lado === lado)) {
        let f = porPart.get(x.participante);
        if (!f) {
          f = { clave: x.participante, razon: x.razon_social, valores: new Map(), total: 0 };
          porPart.set(x.participante, f);
        }
        f.valores.set(x.anio, x);
        f.total += x.cantidad;
        totales[lado].set(x.anio, (totales[lado].get(x.anio) ?? 0) + x.cantidad);
      }
      filas[lado] = [...porPart.values()].sort((a, b) => b.total - a.total);
    }
    return { anios, filas, totales };
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
        Sin posiciones de {prod(producto).label} capturadas.
      </div>
    );
  }

  const seleccionada = (clave: string, lado: LadoPosicion, anio: number) =>
    !!seleccion && seleccion.participante === clave && seleccion.lado === lado &&
    anio >= Number(seleccion.anioDesde) && anio <= Number(seleccion.anioHasta);

  const celdaNum = "px-2 py-1.5 text-right tabular-nums whitespace-nowrap";

  return (
    <div className="bg-white border rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-slate-600">
          <tr>
            <th className="px-3 py-2 text-left sticky left-0 bg-slate-100 min-w-[16rem]">
              Participante <span className="font-normal text-slate-400">· {unidad}</span>
            </th>
            {anios.map((a) => <th key={a} className="px-2 py-2 text-right">{a}</th>)}
          </tr>
        </thead>
        <tbody>
          {(["demanda", "oferta"] as const).map((lado) => (
            <SeccionLado key={lado} lado={lado} anios={anios} filas={filas[lado]}
              total={totales[lado]} decimales={decimales} celdaNum={celdaNum}
              seleccionada={seleccionada} onCelda={onCelda} producto={producto}
              editando={editando} setEditando={setEditando}
              onGuardarCelda={onGuardarCelda} />
          ))}
          <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
            <td className="px-3 py-2 sticky left-0 bg-slate-50">
              Neto <span className="font-normal text-xs text-slate-500">(oferta − demanda)</span>
            </td>
            {anios.map((a) => {
              const neto = (totales.oferta.get(a) ?? 0) - (totales.demanda.get(a) ?? 0);
              return (
                <td key={a} className={`${celdaNum} ${neto < 0 ? "text-rose-700" : neto > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                  {neto === 0 ? "—" : fmt(neto, decimales)}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
      <div className="px-3 py-2 border-t text-xs text-slate-500">
        {guardando && <span className="text-sky-700 font-medium mr-2">Guardando…</span>}
        Clic en una celda para editarla ahí mismo: <b>Enter</b> guarda, <b>Tab</b> guarda y pasa al
        año siguiente, <b>Esc</b> cancela; dejarla vacía la borra. El lápiz la abre en el formulario
        (notas o rango de años). El neto negativo es demanda sin oferta que la cubra.
      </div>
    </div>
  );
}

function SeccionLado({
  lado, anios, filas, total, decimales, celdaNum, seleccionada, onCelda, producto,
  editando, setEditando, onGuardarCelda,
}: {
  lado: LadoPosicion;
  anios: number[];
  filas: { clave: string; razon: string; valores: Map<number, PosicionMercado> }[];
  total: Map<number, number>;
  decimales: number;
  celdaNum: string;
  seleccionada: (clave: string, lado: LadoPosicion, anio: number) => boolean;
  onCelda: (participante: string, lado: LadoPosicion, anio: number) => void;
  producto: ProductoPosicion;
  editando: CeldaId | null;
  setEditando: (c: CeldaId | null) => void;
  onGuardarCelda: (c: CambioCelda) => void;
}) {
  const esDemanda = lado === "demanda";
  const info = LADOS.find((l) => l.key === lado)!;
  return (
    <>
      <tr className={esDemanda ? "bg-rose-50" : "bg-emerald-50"}>
        <td colSpan={anios.length + 1}
          className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${esDemanda ? "text-rose-700" : "text-emerald-700"}`}>
          {info.label} <span className="normal-case font-normal tracking-normal opacity-75">· {info.detalle}</span>
        </td>
      </tr>
      {filas.length === 0 && (
        <tr className="border-t">
          <td colSpan={anios.length + 1} className="px-3 py-2 text-xs text-slate-400">
            Sin {info.label.toLowerCase()} capturada.
          </td>
        </tr>
      )}
      {filas.map((f) => (
        <tr key={f.clave} className="border-t">
          <td className="px-3 py-1.5 sticky left-0 bg-white" title={f.razon}>
            <span className="font-mono text-xs text-slate-500 mr-2">{f.clave}</span>
            <span className="text-slate-800">{corto(f.razon)}</span>
          </td>
          {anios.map((a, i) => {
            const p = f.valores.get(a);
            const sel = seleccionada(f.clave, lado, a);
            if (editando && editando.clave === f.clave && editando.lado === lado && editando.anio === a) {
              return (
                <td key={a} className="px-1 py-0.5 bg-sky-50 ring-2 ring-inset ring-sky-400">
                  <EditorCelda
                    inicial={p ? String(p.cantidad) : ""}
                    onCancelar={() => setEditando(null)}
                    onFormulario={() => { setEditando(null); onCelda(f.clave, lado, a); }}
                    onListo={(texto, siguiente) => {
                      const t = texto.trim().replace(/,/g, "");
                      const valor = t === "" ? null : Number(t);
                      if (valor !== null && !(valor > 0)) return false;   // inválido: sigue editando
                      if (valor === null && p &&
                          !window.confirm(`¿Borrar ${f.clave} ${a} (${lado})?`)) return false;
                      const cambio = valor === null ? !!p : valor !== p?.cantidad;
                      if (cambio) {
                        onGuardarCelda({ participante: f.clave, lado, producto, anio: a, valor, existente: p });
                      }
                      const sig = siguiente ? anios[i + 1] : undefined;
                      setEditando(sig !== undefined ? { clave: f.clave, lado, anio: sig } : null);
                      return true;
                    }}
                  />
                </td>
              );
            }
            return (
              <td key={a} onClick={() => setEditando({ clave: f.clave, lado, anio: a })}
                title={p?.notas ? `${p.notas}` : p ? "Clic para editar" : "Clic para capturar"}
                className={`${celdaNum} cursor-pointer hover:bg-slate-100 ${sel ? "ring-2 ring-inset ring-sky-400" : ""} ${p ? "text-slate-800" : "text-slate-300"}`}>
                {p ? fmt(p.cantidad, decimales) : "·"}
                {p?.notas && <span className="text-sky-500 ml-0.5">*</span>}
              </td>
            );
          })}
        </tr>
      ))}
      <tr className="border-t bg-slate-50 font-medium">
        <td className="px-3 py-1.5 sticky left-0 bg-slate-50 text-slate-600">Total {info.label.toLowerCase()}</td>
        {anios.map((a) => {
          const v = total.get(a) ?? 0;
          return <td key={a} className={`${celdaNum} ${v ? "" : "text-slate-400"}`}>{v ? fmt(v, decimales) : "—"}</td>;
        })}
      </tr>
    </>
  );
}

/** Input en línea de una celda. onListo devuelve false si el valor no es válido. */
function EditorCelda({ inicial, onCancelar, onFormulario, onListo }: {
  inicial: string;
  onCancelar: () => void;
  onFormulario: () => void;
  onListo: (texto: string, siguiente: boolean) => boolean;
}) {
  const [texto, setTexto] = useState(inicial);
  const [error, setError] = useState(false);
  const listo = useRef(false);   // evita guardar dos veces (Enter + blur)
  const terminar = (siguiente: boolean) => {
    if (listo.current) return;
    listo.current = true;
    if (!onListo(texto, siguiente)) {
      listo.current = false;
      setError(true);
    }
  };
  return (
    <div className="flex items-center justify-end gap-0.5">
      <input autoFocus value={texto} inputMode="decimal"
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => { setTexto(e.target.value); setError(false); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); terminar(false); }
          else if (e.key === "Tab" && !e.shiftKey) { e.preventDefault(); terminar(true); }
          else if (e.key === "Escape") { listo.current = true; onCancelar(); }
        }}
        onBlur={() => terminar(false)}
        className={`w-24 rounded border px-1.5 py-0.5 text-sm text-right tabular-nums ${error ? "border-red-400 bg-red-50" : "border-slate-300"}`} />
      <button type="button" title="Abrir en el formulario (notas, rango de años)"
        onMouseDown={(e) => { e.preventDefault(); listo.current = true; onFormulario(); }}
        className="text-slate-400 hover:text-slate-700">
        <PenLine className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

// Mismos colores e iconos que /escenarios: oferta = lo que MPP podría COMPRAR
// (morado, como "Compras"), demanda = lo que MPP podría VENDER (naranja, como "Ventas").
const PRODUCTO_UI: Record<ProductoPosicion, { icon: typeof Zap; activo: string }> = {
  energia: { icon: Zap, activo: "bg-amber-100 text-amber-700 border-transparent" },
  potencia: { icon: Gauge, activo: "bg-indigo-100 text-indigo-700 border-transparent" },
  cel: { icon: Leaf, activo: "bg-emerald-100 text-emerald-700 border-transparent" },
  irec: { icon: Globe, activo: "bg-sky-100 text-sky-700 border-transparent" },
};
const PALETA = [
  "#4f46e5", "#0ea5e9", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#22c55e", "#eab308", "#06b6d4", "#a855f7", "#f97316",
];
const nf = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 2 });

/** Oferta vs demanda por año + neto (oferta − demanda), estilo /escenarios. */
function Grafica({ items, producto, onProducto }: {
  items: PosicionMercado[];
  producto: ProductoPosicion;
  onProducto: (p: ProductoPosicion) => void;
}) {
  const [apilar, setApilar] = useState(false);
  const [mwhH, setMwhH] = useState(false);       // energía en MWh/h promedio (÷ 8,760)
  const [desde, setDesde] = useState<number | null>(null);
  const [hasta, setHasta] = useState<number | null>(null);

  const delProducto = useMemo(() => items.filter((x) => x.producto === producto), [items, producto]);
  const anios = useMemo(() => {
    const ys = delProducto.map((x) => x.anio);
    if (!ys.length) return [];
    const min = Math.min(...ys), max = Math.max(...ys);
    return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  }, [delProducto]);
  const d = desde !== null && anios.includes(desde) ? desde : anios[0];
  const h = hasta !== null && anios.includes(hasta) ? hasta : anios[anios.length - 1];

  const enMwhH = producto === "energia" && mwhH;
  const factor = enMwhH ? 1 / HORAS_ANIO : 1;
  const unidad = enMwhH ? "MWh/h" : prod(producto).unidad;

  // Participantes por volumen total (el color sigue al participante en ambos lados).
  const participantes = useMemo(() => {
    const tot = new Map<string, { razon: string; total: number }>();
    for (const x of delProducto) {
      const t = tot.get(x.participante) ?? { razon: x.razon_social, total: 0 };
      t.total += x.cantidad;
      tot.set(x.participante, t);
    }
    return [...tot.entries()].sort((a, b) => b[1].total - a[1].total)
      .map(([clave, v]) => ({ clave, razon: v.razon }));
  }, [delProducto]);
  const colorDe = (clave: string) =>
    PALETA[Math.max(0, participantes.findIndex((p) => p.clave === clave)) % PALETA.length];

  const puntos = useMemo(() => anios.filter((a) => a >= d && a <= h).map((a) => {
    const fila: Record<string, number | string> = { etiqueta: String(a), oferta: 0, demanda: 0 };
    for (const x of delProducto) {
      if (x.anio !== a) continue;
      const v = x.cantidad * factor;
      if (x.lado === "oferta") fila.oferta = (fila.oferta as number) + v;
      else fila.demanda = (fila.demanda as number) + v;
      const k = `${x.lado}|${x.participante}`;
      fila[k] = ((fila[k] as number | undefined) ?? 0) + v;
    }
    fila.neto = (fila.oferta as number) - (fila.demanda as number);
    return fila;
  }), [anios, d, h, delProducto, factor]);

  const series = useMemo(() => {
    const out: { key: string; lado: LadoPosicion; clave: string; razon: string }[] = [];
    for (const lado of ["oferta", "demanda"] as const) {
      for (const p of participantes) {
        if (delProducto.some((x) => x.lado === lado && x.participante === p.clave)) {
          out.push({ key: `${lado}|${p.clave}`, lado, clave: p.clave, razon: p.razon });
        }
      }
    }
    return out;
  }, [participantes, delProducto]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <div>
          <div className="text-[11px] font-medium text-slate-500 mb-1">Producto a graficar</div>
          <div className="flex gap-2">
            {PRODUCTOS.map((p) => {
              const Icon = PRODUCTO_UI[p.key].icon;
              const active = producto === p.key;
              return (
                <button key={p.key} type="button" onClick={() => onProducto(p.key)} aria-pressed={active}
                  className={`inline-flex items-center gap-1 rounded px-3 py-1.5 text-sm font-semibold border transition ${
                    active ? PRODUCTO_UI[p.key].activo : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                  }`}>
                  <Icon className="w-4 h-4" /> {p.label}
                </button>
              );
            })}
          </div>
        </div>
        {anios.length > 1 && (
          <div>
            <div className="text-[11px] font-medium text-slate-500 mb-1">Años</div>
            <div className="flex items-center gap-1.5 text-sm text-slate-500">
              <select value={d} onChange={(e) => {
                const v = Number(e.target.value);
                setDesde(v);
                if (v > h) setHasta(v);
              }} className="border border-slate-200 rounded px-3 py-1.5 text-sm bg-white">
                {anios.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              a
              <select value={h} onChange={(e) => {
                const v = Number(e.target.value);
                setHasta(v);
                if (v < d) setDesde(v);
              }} className="border border-slate-200 rounded px-3 py-1.5 text-sm bg-white">
                {anios.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>
        )}
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 select-none pb-1.5">
          <input type="checkbox" checked={apilar} onChange={(e) => setApilar(e.target.checked)} className="w-4 h-4" />
          Apilar por participante
        </label>
        {producto === "energia" && (
          <label className="inline-flex items-center gap-2 text-sm text-slate-600 select-none pb-1.5">
            <input type="checkbox" checked={mwhH} onChange={(e) => setMwhH(e.target.checked)} className="w-4 h-4" />
            Energía en MWh/h (promedio, ÷ 8,760)
          </label>
        )}
      </div>

      <div className="bg-white border rounded-lg p-4">
        {puntos.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            Sin posiciones de {prod(producto).label} para graficar.
          </div>
        ) : (
          <>
            <div className="text-xs text-slate-500 mb-2">
              Unidad: {unidad} · morado = oferta (MPP compraría) · naranja = demanda (MPP vendería) ·
              línea verde = neto
            </div>
            <div style={{ width: "100%", height: 380 }}>
              <ResponsiveContainer>
                <ComposedChart data={puntos} barGap={0} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} width={72} tickFormatter={(v) => nf.format(v as number)} />
                  <Tooltip formatter={(v: number, name: string) => [`${nf.format(v)} ${unidad}`, name]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <ReferenceLine y={0} stroke="#cbd5e1" />
                  {apilar ? (
                    series.map((s) => (
                      <Bar key={s.key} dataKey={s.key} stackId={s.lado} fill={colorDe(s.clave)}
                        fillOpacity={s.lado === "demanda" ? 0.6 : 1}
                        name={`${s.lado === "oferta" ? "Oferta" : "Demanda"} · ${s.clave} ${corto(s.razon)}`} />
                    ))
                  ) : (
                    <>
                      <Bar dataKey="oferta" name="Oferta" stackId="O" fill="#4f46e5" />
                      <Bar dataKey="demanda" name="Demanda" stackId="D" fill="#f59e0b" />
                    </>
                  )}
                  <Line type="monotone" dataKey="neto" name="Neto (oferta − demanda)"
                    stroke="#059669" strokeWidth={2.5} dot={puntos.length <= 60 ? { r: 3 } : false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Neto negativo = el mercado pide más de lo que se ofrece (oportunidad de venta para MPP);
              positivo = sobra oferta.
              {apilar && " Apilado: barras sólidas = oferta, claras = demanda; el color sigue al participante."}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Buscador del catálogo de participantes (clave, razón social o alias) + alta manual. */
function ComboParticipante({ opciones, cargando, valor, onChange, onAlta }: {
  opciones: ParticipanteMem[];
  cargando: boolean;
  valor: string;
  onChange: (clave: string) => void;
  onAlta: (p: ParticipanteMem) => void;
}) {
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(false);
  const [alta, setAlta] = useState(false);
  const caja = useRef<HTMLDivElement>(null);

  const elegido = opciones.find((o) => o.clave === valor);

  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, []);

  const filtradas = useMemo(() => {
    const t = sinAcentos(texto.trim());
    const lista = t
      ? opciones.filter((o) => sinAcentos(`${o.clave} ${o.razon_social} ${o.alias ?? ""}`).includes(t))
      : opciones;
    return lista.slice(0, 60);
  }, [opciones, texto]);

  if (alta) {
    return (
      <AltaParticipante
        claveInicial={/^[a-z]\d{3}$/i.test(texto.trim()) ? texto.trim().toUpperCase() : ""}
        razonInicial={/^[a-z]\d{3}$/i.test(texto.trim()) ? "" : texto.trim()}
        onCancelar={() => setAlta(false)}
        onListo={(p) => { setAlta(false); setTexto(""); onAlta(p); }}
      />
    );
  }

  return (
    <div ref={caja} className="relative">
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
        <input
          value={abierto ? texto : elegido ? `${elegido.clave} · ${elegido.razon_social}` : texto}
          onFocus={() => { setAbierto(true); setTexto(""); }}
          onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
          placeholder={cargando ? "Cargando catálogo…" : "Clave (C006), razón social o alias"}
          className="w-full rounded border border-slate-300 pl-8 pr-2 py-1.5 text-sm"
        />
      </div>
      {abierto && (
        <ul className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded border border-slate-200 bg-white shadow-lg text-sm">
          {filtradas.length === 0 && (
            <li className="px-3 py-2 text-slate-400 text-xs">Sin coincidencias en el catálogo.</li>
          )}
          {filtradas.map((o) => (
            <li key={o.clave}>
              <button type="button"
                onClick={() => { onChange(o.clave); setAbierto(false); setTexto(""); }}
                className={`w-full text-left px-3 py-1.5 hover:bg-slate-50 ${o.clave === valor ? "bg-sky-50" : ""}`}>
                <span className="font-mono text-xs text-slate-500 mr-2">{o.clave}</span>
                <span className="text-slate-800">{o.razon_social}</span>
                {o.alias && <span className="text-slate-400"> ({o.alias})</span>}
                {o.posiciones > 0 && (
                  <span className="ml-2 text-[11px] text-sky-600">{o.posiciones} pos.</span>
                )}
              </button>
            </li>
          ))}
          <li className="border-t">
            <button type="button" onClick={() => { setAbierto(false); setAlta(true); }}
              className="w-full text-left px-3 py-2 text-xs text-sky-700 hover:bg-sky-50 inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> ¿No está? Agregar participante al catálogo
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

function AltaParticipante({ claveInicial, razonInicial, onCancelar, onListo }: {
  claveInicial: string;
  razonInicial: string;
  onCancelar: () => void;
  onListo: (p: ParticipanteMem) => void;
}) {
  const [clave, setClave] = useState(claveInicial);
  const [razon, setRazon] = useState(razonInicial);
  const alta = useMutation({
    mutationFn: () => altaParticipanteMem({ clave: clave.trim().toUpperCase(), razon_social: razon.trim() }),
    onSuccess: onListo,
  });
  const ok = /^[a-z]\d{3}$/i.test(clave.trim()) && razon.trim().length >= 2;
  return (
    <div className="rounded border border-sky-200 bg-sky-50 p-2 space-y-2">
      <div className="flex gap-2">
        <input autoFocus value={clave} maxLength={4} onChange={(e) => setClave(e.target.value)}
          placeholder="G123"
          className="w-20 rounded border border-slate-300 px-2 py-1 text-sm font-mono uppercase" />
        <input value={razon} maxLength={250} onChange={(e) => setRazon(e.target.value)}
          placeholder="Razón social"
          className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm" />
      </div>
      <div className="flex items-center gap-2">
        <button type="button" disabled={!ok || alta.isPending} onClick={() => alta.mutate()}
          className="inline-flex items-center gap-1 rounded bg-slate-900 text-white px-2.5 py-1 text-xs hover:bg-slate-800 disabled:opacity-50">
          <Plus className="w-3.5 h-3.5" /> {alta.isPending ? "Agregando…" : "Agregar"}
        </button>
        <button type="button" onClick={onCancelar}
          className="rounded border bg-white text-slate-600 px-2.5 py-1 text-xs hover:bg-slate-50">
          Cancelar
        </button>
        <span className="text-[11px] text-slate-500">Clave CENACE: letra + 3 dígitos.</span>
      </div>
      {alta.isError && <div className="text-xs text-red-600">{(alta.error as Error).message}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Etiqueta({ children }: { children: ReactNode }) {
  return <label className="block text-xs font-medium text-slate-500 mb-1">{children}</label>;
}

function Alerta({ texto }: { texto: string }) {
  return <div className="bg-red-50 border border-red-200 text-red-700 rounded p-2.5 text-sm">{texto}</div>;
}

function fmt(v: number, decimales: number): string {
  return v.toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: decimales });
}

function sinAcentos(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

/** Razón social sin el tipo de sociedad, para que la columna no se coma la tabla.
 *  El tipo debe terminar en separador: sin eso "SC SAAVI…" se cortaba en el "SA" de SAAVI. */
function corto(razon: string): string {
  return razon.replace(
    /,?\s+(S\.?\s?A\.?\s?P\.?\s?I\.?|S\.?\s?A\.?|S\.?\s?de\s?R\.?\s?L\.?)(?=[\s,.]|$)(\s+de\s+C\.?\s?V\.?)?.*$/i, "",
  ).trim() || razon;
}
